import { useMemo, useState, Fragment } from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useTranslation } from 'react-i18next';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import { Button } from '@/components/ui/button';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';

type Props<TData> = {
    columns: ColumnDef<TData, unknown>[];
    data?: { items?: TData[]; totalCount: number };
    page: number;
    setPage: (page: number) => void;
    paginationStep?: number;
    isLoadingPage?: boolean;
    getRowId: (row: TData) => string;
    renderExpandedContent: (row: TData) => React.ReactNode;
    onRowClick?: (row: TData) => void;
};

const SuperAdminExpandableDataTable = <TData,>({
    columns,
    data,
    page,
    setPage,
    paginationStep = 10,
    isLoadingPage,
    getRowId,
    renderExpandedContent,
    onRowClick,
}: Props<TData>) => {
    const { t } = useTranslation();
    const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

    const tableColumns = useMemo<ColumnDef<TData, unknown>[]>(
        () => [
            {
                id: 'expand',
                header: '',
                size: 48,
                cell: ({ row }) => {
                    const rowId = getRowId(row.original);
                    const isExpanded = expandedRowIds.includes(rowId);
                    return (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={(event) => {
                                event.stopPropagation();
                                setExpandedRowIds((current) =>
                                    isExpanded
                                        ? current.filter((id) => id !== rowId)
                                        : [...current, rowId],
                                );
                            }}
                        >
                            <ArrowDownIcon
                                className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                        </Button>
                    );
                },
            },
            ...columns,
        ],
        [columns, expandedRowIds, getRowId],
    );

    const table = useReactTable({
        data: data?.items ?? [],
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <div className="rounded-semi-xl border border-popover-100 bg-popover-10">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} style={{ maxWidth: header.column.columnDef.size }}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => {
                                const rowId = getRowId(row.original);
                                const isExpanded = expandedRowIds.includes(rowId);
                                return (
                                    <Fragment key={row.id}>
                                        <TableRow
                                            className={onRowClick ? 'cursor-pointer' : undefined}
                                            onClick={() => onRowClick?.(row.original)}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {isExpanded && (
                                            <TableRow key={`${row.id}-expanded`}>
                                                <TableCell colSpan={tableColumns.length}>
                                                    {renderExpandedContent(row.original)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                                    {isLoadingPage ? t('elements.table.body.loading') : t('elements.table.body.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {data?.totalCount && data.totalCount > paginationStep && (
                <ETablePaginationWithApi
                    page={page}
                    setPage={setPage}
                    totalCount={Math.ceil(data.totalCount / paginationStep)}
                />
            )}
        </>
    );
};

export default SuperAdminExpandableDataTable;
